import { view, site, assets, i18next, blocks, hdrm, textFormatting } from '@yola/ws-sdk';
import store from 'src/js/store';
import setDisabledStatus from 'src/js/modules/view/actions/set-disabled-status';
import blobToBase64 from '../../extensions/registry/ws-icon/helpers/blob-to-base64';
import permissions from '../../extensions/registry/ws-image/constants/permissions';
import handleClipboardPermissionDenied from '../../extensions/registry/ws-image/helpers/handle-clipboard-permission-denied';
import showCopyToClipboardErrorDialog from '../../extensions/registry/ws-image/helpers/show-copy-to-clipboard-error-dialog';
import { BACKGROUND_IMAGE_ATTRIBUTE } from '../constants/block-background-attributes';
import getBackgroundImageSourcesSet from './get-background-image-sources-set';
import getBackgroundImageOrigSource from './get-background-image-orig-source';

const getBase64 = async (src) => {
  const res = await fetch(assets.helpers.addBaseHref(src));
  const blob = await res.blob();
  const base64 = await blobToBase64(blob);

  return base64;
};

const prepareBgImageToCopy = async (liveElement) => {
  const preparedBgImageSrcset = [];
  const srcset = getBackgroundImageSourcesSet(liveElement);
  const origSrc = getBackgroundImageOrigSource(liveElement);

  // eslint-disable-next-line no-restricted-syntax
  for (const part of srcset) {
    if (part.mediaQuery && part.src) {
      // eslint-disable-next-line no-await-in-loop
      const base64 = await getBase64(part.src);
      preparedBgImageSrcset.push({ mediaQuery: part.mediaQuery, src: base64 });
    }
  }

  if (origSrc) {
    const base64 = await getBase64(origSrc);
    preparedBgImageSrcset.push({ mediaQuery: null, src: base64 });
  }

  return preparedBgImageSrcset;
};

const prepareItemsToCopy = async (items) => {
  const preparedItems = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const item of items) {
    const src = item.getAttribute('src');

    if (src) {
      // eslint-disable-next-line no-await-in-loop
      const base64 = await getBase64(src);
      preparedItems[src] = base64;
    }
  }

  return preparedItems;
};

const prepareCssRuleToCopy = (rule) => {
  const { style, selectorText } = rule;
  const props = [];

  Array.from(style).forEach((name) => {
    props.push(`${name}: ${style.getPropertyValue(name)};`);
  });

  return {
    selector: selectorText,
    property: props.join(' '),
  };
};

const prepareCustomColorsToCopy = (element) => {
  const stylesheet = textFormatting.helpers.getCustomTextColorsStylesheet();
  const rootCssVariablesMap = textFormatting.helpers.getCssRootVariablesMap();

  const { cssRules } = stylesheet;
  const cssRule = Array.from(cssRules).find((rule) => rule.selectorText.includes(element.id));
  const cssRuleObj = prepareCssRuleToCopy(cssRule);
  const filteredRootCssVariables = Object.entries(rootCssVariablesMap).filter(([name]) =>
    cssRuleObj.property.includes(name)
  );

  return {
    cssRuleObj,
    cssVariables: filteredRootCssVariables,
    textVariables: Array.from(cssRule.style),
  };
};

const copyBlockToClipboard = async (elementId) => {
  const { status } = await handleClipboardPermissionDenied(
    permissions.features.CLIPBOARD_READ,
    false
  );

  if (status === permissions.statuses.DENIED) {
    return;
  }

  const element = view.accessors.getStaticElement(elementId);
  const clonedElement = element.cloneNode(true);
  const originalOptionalChildren = blocks.accessors.getOptionalChildrenData(element);
  let hasHiddenChildren = false;

  originalOptionalChildren.forEach((child) => {
    if (!child.isEnabled) {
      hasHiddenChildren = true;
      hdrm.helpers.updateChildrenPresence(clonedElement, child.querySelector, true, child.id);
    }
  });

  const siteId = site.accessors.getSiteId();
  const pageId = site.accessors.getActivePageId();
  const images = clonedElement.querySelectorAll('img');
  const icons = clonedElement.querySelectorAll('ws-icon');
  const customColorWrappers = clonedElement.querySelectorAll('ws-color');
  const withBgImage = clonedElement.hasAttribute(BACKGROUND_IMAGE_ATTRIBUTE);
  const dataToCopy = {
    blockElement: clonedElement.outerHTML,
    ...(hasHiddenChildren && { originalOptionalChildren }),
    siteId,
    pageId,
  };

  store.dispatch(view.actions.setLoadedStatus(i18next.t('Copying to clipboard...')));
  store.dispatch(setDisabledStatus(true));

  if (images.length) {
    dataToCopy.images = await prepareItemsToCopy(images);
  }

  if (icons.length) {
    dataToCopy.icons = await prepareItemsToCopy(icons);
  }

  if (customColorWrappers.length) {
    dataToCopy.customColors = prepareCustomColorsToCopy(element);
  }

  if (withBgImage) {
    const liveElement = view.accessors.getLiveElement(elementId);
    dataToCopy.bgImage = await prepareBgImageToCopy(liveElement);
  }

  const type = 'text/plain';
  const blob = new Blob([JSON.stringify(dataToCopy)], { type });
  let clipboardItem;

  try {
    clipboardItem = new ClipboardItem({ [type]: blob });
    await navigator.clipboard.write([clipboardItem]);
  } catch (e) {
    console.error(e);

    if (e.message.includes('Document is not focused.')) {
      showCopyToClipboardErrorDialog();
    }

    store.dispatch(view.actions.setLoadedStatus(true));
    store.dispatch(setDisabledStatus(false));

    return;
  }

  store.dispatch(view.actions.setLoadedStatus(true));
  store.dispatch(setDisabledStatus(false));
};

export default copyBlockToClipboard;
